import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Users (Admin)
export const getUsers = (status) => api.get('/admin/users', { params: { status } });
export const updateUser = (userId, data) => api.put(`/admin/users/${userId}`, data);
export const deleteUser = (userId) => api.delete(`/admin/users/${userId}`);
export const createUserByAdmin = (data) => api.post('/admin/users', data);

// Projects
export const getProjects = (status) => api.get('/projects', { params: { status } });
export const getProject = (projectId) => api.get(`/projects/${projectId}`);
export const createProject = (data) => api.post('/projects', data);
export const requestProject = (data) => api.post('/projects/request', data);
export const updateProject = (projectId, data) => api.put(`/projects/${projectId}`, data);
export const deleteProject = (projectId) => api.delete(`/projects/${projectId}`);

// Profile
export const updateProfile = (data) => api.put('/auth/profile', data);

// Payment Methods
export const getPaymentMethods = () => api.get('/payment-methods');
export const addPaymentMethod = (data) => api.post('/payment-methods', data);
export const deletePaymentMethod = (methodId) => api.delete(`/payment-methods/${methodId}`);
export const setDefaultPayment = (methodId) => api.put(`/payment-methods/${methodId}/default`);

// Social Media Connections
export const getSocialConnections = () => api.get('/social-connections');
export const connectSocialAccount = (platform, data) => {
  const formData = new FormData();
  formData.append('platform_username', data.platform_username);
  if (data.platform_email) formData.append('platform_email', data.platform_email);
  return api.post(`/social-connections/${platform}`, formData);
};
export const disconnectSocialAccount = (platform) => api.delete(`/social-connections/${platform}`);

// LinkedIn OAuth
export const startLinkedInOAuth = () => api.get('/integrations/linkedin/start');

// Stripe Payments
export const getStripeConfig = () => api.get('/payments/config');
export const createPaymentCheckout = (data) => api.post('/payments/checkout', data);
export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);
export const getPaymentHistory = () => api.get('/payments/history');
export const getProjectPayments = (projectId) => api.get(`/payments/project/${projectId}`);
export const setProjectPaymentAmount = (projectId, amount) => api.put(`/admin/projects/${projectId}/payment?amount=${amount}`);

// Admin CSV Upload
export const uploadUsersCSV = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/users/upload-csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Site Documents (Admin)
export const createSiteDocument = (data) => api.post('/admin/documents', data);
export const getAdminDocuments = (docType) => api.get('/admin/documents', { params: { doc_type: docType } });
export const updateSiteDocument = (docId, data) => api.put(`/admin/documents/${docId}`, data);
export const deleteSiteDocument = (docId) => api.delete(`/admin/documents/${docId}`);
export const getDocumentInteractions = (docId) => api.get(`/admin/documents/${docId}/interactions`);

// Site Documents (Client)
export const getClientDocuments = (docType) => api.get('/documents', { params: { doc_type: docType } });
export const getDocumentDetail = (docId) => api.get(`/documents/${docId}`);
export const signDocument = (docId) => api.post(`/documents/${docId}/sign`);
export const submitQuestionnaire = (docId, responses) => api.post(`/documents/${docId}/submit`, responses);
export const getMyDocumentStatus = () => api.get('/documents/my-status');

// Files
export const getProjectFiles = (projectId) => api.get(`/projects/${projectId}/files`);
export const uploadFile = (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/projects/${projectId}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const downloadFile = (fileId) => api.get(`/files/${fileId}/download`, { responseType: 'blob' });
export const deleteFile = (fileId) => api.delete(`/files/${fileId}`);

// Messages
export const getProjectMessages = (projectId) => api.get(`/projects/${projectId}/messages`);
export const sendMessage = (projectId, content) => api.post(`/projects/${projectId}/messages`, { content, project_id: projectId });
export const getUnreadCount = () => api.get('/messages/unread-count');

// Contracts
export const getProjectContracts = (projectId) => api.get(`/projects/${projectId}/contracts`);
export const createContract = (projectId, data) => api.post(`/projects/${projectId}/contracts`, { ...data, project_id: projectId });
export const updateContract = (contractId, data) => api.put(`/contracts/${contractId}`, data);

export default api;
